import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Share2, Clock } from "lucide-react";
import { getBlogs } from "../services/services";

const IMAGE = "https://miniapptech.in/assets/img/blog";
const VIDEO = "https://miniapptech.in/assets/img/blog_video";

const BlogPost = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [videoFinished, setVideoFinished] = useState(false);

  useEffect(() => {
    const fetchBlog = async () => {
      const res = await getBlogs();

      if (res.status) {
        const found = res.data.find(
          (p) => String(p.blog_detail_id) === String(id),
        );

        setPost(found);
      }
    };

    fetchBlog();
  }, [id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const getEmbedUrl = (url) => {
    if (!url) return "";

    if (url.includes("youtu.be")) {
      const videoId = url.split("/").pop().split("?")[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }

    if (url.includes("youtube.com")) {
      const videoId = new URL(url).searchParams.get("v");
      return `https://www.youtube.com/embed/${videoId}`;
    }

    return url;
  };

  const slides = useMemo(() => {
    if (!post) return [];

    const arr = [];

    if (post?.video?.trim()) {
      arr.push({ type: "video", value: post.video });
    }

    if (Array.isArray(post?.image)) {
      post.image.forEach((img) => {
        arr.push({ type: "image", value: img });
      });
    }

    return arr;
  }, [post]);

  // AUTO SLIDER
useEffect(() => {
  if (slides.length <= 1) return;

  if (
    slides[currentSlide]?.type === "video" &&
    !videoFinished
  ) {
    return;
  }

  const interval = setInterval(() => {
    setCurrentSlide((prev) =>
      prev === slides.length - 1 ? 0 : prev + 1
    );
  }, 3000);

  return () => clearInterval(interval);
}, [slides, currentSlide, videoFinished]);

  if (!post) {
    return (
      <div className="pt-32 pb-20 text-center min-h-screen">
        <h2 className="text-3xl font-bold mb-4">Post Not Found</h2>
        <Link to="/blog" className="text-primary font-bold">
          Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pt-24 pb-20 bg-white min-h-screen"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Navigation */}
        <Link
          to="/blog"
          className="inline-flex items-center text-primary font-bold hover:gap-2 transition-all mb-8 group"
        >
          <ArrowLeft
            size={18}
            className="mr-2 transition-transform group-hover:-translate-x-1"
          />
          Back to Insights
        </Link>

        {/* Header */}
        <header className="mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="bg-primary/10 text-primary text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                {post.category}
              </span>
              <span className="flex items-center gap-1 text-gray-500 text-sm">
                <Clock size={14} /> 5 min read
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-dark mb-8 leading-tight">
              {post.title}
            </h1>

            <div className="flex items-center justify-between border-b border-gray-100 pb-8">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mr-4">
                  <span className="text-primary font-bold text-lg">
                    {post.author.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="font-bold text-dark">{post.author}</p>
                  <div className="flex items-center text-sm text-gray-500 gap-3">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} /> {post.date}
                    </span>
                  </div>
                </div>
              </div>

              <button className="flex items-center gap-2 text-gray-400 hover:text-primary transition-colors">
                <Share2 size={20} />
                <span className="hidden sm:inline text-sm font-medium">
                  Share
                </span>
              </button>
            </div>
          </motion.div>
        </header>

        {/* Featured Image */}

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="mb-12 rounded-3xl overflow-hidden shadow-2xl"
        >
          {slides.length > 0 && (
            <>
              {slides[currentSlide]?.type === "video" ? (
                slides[currentSlide].value?.includes("youtube") ||
                slides[currentSlide].value?.includes("youtu.be") ? (
                  <iframe
                    src={getEmbedUrl(slides[currentSlide].value)}
                    className="w-full h-[300px] md:h-[500px]"
                    allowFullScreen
                    title="video"
                  />
                ) : (
                  <video
                    muted
                    autoPlay
                    
                    controls
                    onEnded={() => {
                      setVideoFinished(true);
                      setCurrentSlide((prev) =>
                        prev === slides.length - 1 ? 0 : prev + 1,
                      );
                    }}
                    className="w-full h-[300px] md:h-[500px] object-cover"
                  >
                    <source
                      src={`${VIDEO}/${slides[currentSlide].value}`}
                      type="video/mp4"
                    />
                  </video>
                )
              ) : (
                <img
                  src={`${IMAGE}/${slides[currentSlide].value}`}
                  className="w-full h-[300px] md:h-[500px] object-cover"
                  alt="blog"
                />
              )}

              {/* DOT INDICATORS */}
              {slides.length > 1 && (
                <div className="flex justify-center gap-3 mt-4">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-3 h-3 rounded-full transition-all ${
                        currentSlide === index
                          ? "bg-primary scale-110"
                          : "bg-gray-300"
                      }`}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </motion.div>

        {/* Content */}
        <article className="prose prose-lg max-w-none prose-primary px-2">
          <div
            className="text-gray-700 leading-relaxed space-y-6"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </article>

        {/* Footer Navigation */}
        <div className="mt-20 pt-10 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="text-center sm:text-left">
            <h4 className="font-bold text-dark mb-1">Want more insights?</h4>
            <p className="text-gray-500 text-sm">
              Subscribe to our newsletter for weekly tech updates.
            </p>
          </div>
          <button className="bg-primary text-white px-8 py-3 rounded-xl font-bold hover:bg-secondary transition-all shadow-lg hover:shadow-primary/25">
            Subscribe Now
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default BlogPost;
